import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { CreatorAccount, IntakeSubmission } from '../../lib/types';
import styles from './Dashboard.module.css';

type Tab = 'discovery' | 'intake' | 'active';
const TABS: readonly Tab[] = ['discovery', 'intake', 'active'] as const;

function tabFromParam(raw: string | null): Tab | null {
  return (TABS as readonly string[]).includes(raw ?? '') ? (raw as Tab) : null;
}

export function AdminTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const intakeBadge = useQuery({
    queryKey: ['admin', 'applications'],
    queryFn: () => api.get<IntakeSubmission[]>('/api/admin/applications'),
  });
  const acBadge = useQuery({
    queryKey: ['admin', 'creators'],
    queryFn: () => api.get<CreatorAccount[]>('/api/admin/creators'),
  });

  const pendingIntake = (intakeBadge.data ?? []).filter((s) => s.status === 'pending_review')
    .length;
  const acCount = (acBadge.data ?? []).length;

  const onDashboard = location.pathname === '/admin';

  let active: Tab;
  if (onDashboard) {
    active = tabFromParam(params.get('tab')) ?? 'discovery';
  } else if (location.pathname.startsWith('/admin/creators')) {
    active = 'active';
  } else {
    active = 'discovery';
  }

  function setTab(t: Tab) {
    if (onDashboard) {
      const next = new URLSearchParams(params);
      if (t === 'discovery') next.delete('tab');
      else next.set('tab', t);
      setParams(next, { replace: true });
    } else {
      navigate(t === 'discovery' ? '/admin' : `/admin?tab=${t}`);
    }
  }

  return (
    <div className={styles.tabBar}>
      <button
        type="button"
        className={`${styles.tabBtn} ${active === 'discovery' ? styles.tabActive : ''}`}
        onClick={() => setTab('discovery')}
      >
        Creator Discovery
      </button>
      <button
        type="button"
        className={`${styles.tabBtn} ${active === 'intake' ? styles.tabActive : ''}`}
        onClick={() => setTab('intake')}
      >
        Intake Submissions
        {pendingIntake > 0 && <span className={styles.tabBadge}>{pendingIntake}</span>}
      </button>
      <button
        type="button"
        className={`${styles.tabBtn} ${active === 'active' ? styles.tabActive : ''}`}
        onClick={() => setTab('active')}
      >
        Active Creators
        {acCount > 0 && <span className={styles.tabBadge}>{acCount}</span>}
      </button>
    </div>
  );
}

export type { Tab as AdminTab };
